import {useCallback, useEffect, useMemo, useState} from 'react'

import {useFieldConfigurationContext} from '../../../configuration/useConfigurationContext'
import {getFieldErrors} from '../../../form/errors/getFieldErrors'
import {useDeepCompareMemo} from '../../../form/useFormEffect'
import {useOrderProcessFormContext} from '../../../form/useOrderProcessFormContext'
import {OrderDto} from '../../types'
import {ChangeableFields} from '../changeableFields'
import {mapOrderFormToOrderChangeRequest} from '../orderChangeRequest/mapOrderFormToOrderChangeRequest'
import {OrderChangeRequestPayload} from '../orderChangeRequest/types'

export type OrderChangeStateProps = {
  /** Order containing change information in orderManagement.isChangeable */
  order: OrderDto
  changeableFields: ChangeableFields
}

export const useOrderChangeState = ({order, changeableFields}: OrderChangeStateProps) => {
  const loadSpacingConfiguration = useFieldConfigurationContext('loadSpacing')
  const [isChangeable, setIsChangeable] = useState(order.orderManagement?.isChangeable ?? false)
  const {
    formState: {errors},
    getValues
  } = useOrderProcessFormContext()

  /**
   * Effect: Update changeability status when order data changes.
   * Ensures context reflects latest backend state for order modifications.
   */
  useEffect(() => {
    setIsChangeable(order.orderManagement?.isChangeable ?? false)
  }, [order.orderManagement?.isChangeable])

  /**
   * Calculate whether changes can be submitted.
   * Requires: order is changeable AND at least one field modified AND no validation errors.
   *
   * Checks validation errors for all changeable dirty fields and their nested subfields.
   */
  const canSubmitChanges = useDeepCompareMemo(() => {
    const dirtyChangeableFields = changeableFields
      .filter((field) => field.state !== 'notChangeable')
      .filter((field) => field.isDirty)

    const hasChanges = dirtyChangeableFields.length > 0

    const hasErrors = dirtyChangeableFields.some(
      (field) => getFieldErrors(errors, field.fieldName).length > 0
    )

    return isChangeable && hasChanges && !hasErrors
  }, [isChangeable, changeableFields, errors])

  /**
   * Submit handler that prepares the order change request and delegates to user-provided handler.
   * Maps current form values and changed fields to API payload structure.
   */
  const submit = useCallback(
    <T>(handleRequest: (request: OrderChangeRequestPayload) => T): T => {
      const formData = getValues()
      const dirtyFields = changeableFields
        .filter((field) => field.isDirty)
        .map((field) => field.fieldName)

      const request = mapOrderFormToOrderChangeRequest(formData, {
        order,
        changedFields: dirtyFields,
        loadSpacingConfiguration
      })

      return handleRequest(request)
    },
    [order, changeableFields, getValues, loadSpacingConfiguration]
  )

  return useMemo(
    () => ({
      processData: order, // Common field across all order processes (OrderProcessContext)
      order, // Deprecated alias - prefer processData
      isChangeable,
      canSubmitChanges,
      submit
    }),
    [order, isChangeable, canSubmitChanges, submit]
  )
}
